import { Link } from '@remix-run/react';
import dayjs from 'dayjs';
import classnames from 'classnames';
import { Swiper, SwiperSlide } from 'swiper/react';
import {
    Flex,
    useStyles,
    Button,
    Label,
    useTheme,
} from '@archipro-design/aria';
import { ArrowNextLine, ArrowPrevLine } from '@archipro-design/icons';
import { useTracker } from '@archipro-website/tracker';
import type { ArticleCarouselDesktopProps } from '../../type';
import * as S from './ArticlesCarousel.style';
import { NarrowContent } from '@modules/articles/component/page-layout/PageLayout.style';
import ArchizeenTile from '~/modules/tile/component/archizeen-tile/ArchizeenImpressionTile';
import { getImagesSizes } from '~/utils/image-sizes-utils';
import { ArticleCarouselPlaceholderDesktop } from '../article-tile-skeleton/ArticleCarouselPlaceholder.desktop';
import { useTileHotButtons } from '~/modules/root';
import { useHandleFollow } from '~/modules-rocco/design-board/hook/use-handle-follow';

const SLIDES_PER_VIEW = 3;

const ArticleCarousel: React.FC<ArticleCarouselDesktopProps> = ({
    articleTiles,
    title,
    articleType,
}) => {
    const { css } = useStyles();
    const tracker = useTracker();
    const theme = useTheme();
    const { handleFollow, isInDesignBoard } = useHandleFollow();
    const { getTileTopActions, hotButtonModal } = useTileHotButtons({
        itemType: 'article',
        itemIDs: articleTiles.map((p) => p.ID),
    });

    return !articleTiles ? (
        <ArticleCarouselPlaceholderDesktop title={title} />
    ) : (
        <div className={css(S.Wrap)}>
            <div className={css(NarrowContent)}>
                <Label className={css(S.TitleWrap)} variant="01">
                    {title}
                </Label>
                <div className={css(S.SliderWrap)}>
                    <Swiper
                        slidesPerView={SLIDES_PER_VIEW}
                        slidesPerGroup={SLIDES_PER_VIEW}
                        navigation={{
                            nextEl: `.slider-button-next-${articleType}`,
                            prevEl: `.slider-button-prev-${articleType}`,
                        }}
                        allowSlideNext={true}
                        allowSlidePrev={true}
                        loop={false}
                        className={css(S.Slider)}
                    >
                        {articleTiles.map((article) => {
                            const {
                                link,
                                ID,
                                image,
                                title,
                                date,
                                size,
                                label,
                                variant,
                                variables,
                            } = article;

                            return (
                                <SwiperSlide
                                    key={article.ID}
                                    className={css(S.Slide)}
                                >
                                    <div className={css(S.SlideInner)}>
                                        <ArchizeenTile
                                            key={ID}
                                            as={Link}
                                            to={link}
                                            prefetch="intent"
                                            onFavourite={(e) => {
                                                e.stopPropagation();
                                                e.preventDefault();
                                                handleFollow(
                                                    { id: ID },
                                                    'ArticlePin'
                                                );
                                            }}
                                            size={size}
                                            label={label}
                                            variant={variant}
                                            variables={variables}
                                            isFavourited={isInDesignBoard(
                                                ID,
                                                'ArticlePin'
                                            )}
                                            date={dayjs(date).format(
                                                'DD MMMM YYYY'
                                            )}
                                            onClick={() => {
                                                tracker.log(
                                                    `Article${articleType}Click`,
                                                    {
                                                        url: new URL(
                                                            window.location.href
                                                        ),
                                                        targetTracker:
                                                            'archiproTracker',
                                                        data: {
                                                            ExtraData:
                                                                article.ID,
                                                        },
                                                    }
                                                );
                                            }}
                                            image={{
                                                src: image,
                                                alt: title,
                                                height: 280,
                                                width: 455,
                                                sizes: getImagesSizes(455),
                                            }}
                                            title={title}
                                            impression={{
                                                professionalID:
                                                    article.professionalID,
                                                itemID: ID,
                                                type: articleType,
                                            }}
                                            topActions={getTileTopActions({
                                                item: article,
                                            })}
                                            articleId={ID}
                                        />
                                    </div>
                                </SwiperSlide>
                            );
                        })}
                    </Swiper>
                    {articleTiles.length > SLIDES_PER_VIEW ? (
                        <Flex
                            hAlign="end"
                            gap="space-16"
                            className={css(S.SliderControls)}
                        >
                            <Button
                                className={classnames(
                                    css(S.ButtonSlider),
                                    `slider-button-prev-${articleType}`
                                )}
                                size={30}
                                variables={{
                                    iconPadding: 0,
                                    primaryOutlinedButtonBorderNormal:
                                        '1px solid transparent',
                                    primaryOutlinedButtonBorderHover:
                                        '1px solid transparent',
                                    primaryOutlinedButtonBorderDisable:
                                        '1px solid transparent',

                                    primaryOutlinedButtonBackgroundColorHover:
                                        'transparent',
                                }}
                                color={'primary'}
                                variant={'outlined'}
                                icon={<ArrowPrevLine />}
                            />
                            <Button
                                className={classnames(
                                    css(S.ButtonSlider),
                                    `slider-button-next-${articleType}`
                                )}
                                size={30}
                                variables={{
                                    iconPadding: 0,
                                    primaryOutlinedButtonBorderNormal:
                                        '1px solid transparent',
                                    primaryOutlinedButtonBorderHover:
                                        '1px solid transparent',
                                    primaryOutlinedButtonBorderDisable:
                                        '1px solid transparent',
                                    primaryOutlinedButtonTextColorHover:
                                        theme?.siteVariables?.colors?.charcoal[
                                            '000'
                                        ],
                                    primaryOutlinedButtonBackgroundColorHover:
                                        'transparent',
                                }}
                                color={'primary'}
                                variant={'outlined'}
                                icon={<ArrowNextLine />}
                            />
                        </Flex>
                    ) : (
                        <div className={css(S.NoSliderClass)} />
                    )}
                </div>
            </div>
            {hotButtonModal}
        </div>
    );
};

export default ArticleCarousel;
